import React, { useEffect, useState } from 'react';

const SHOPIFY_URL = 'https://shyft-store.myshopify.com/products/shyft-christmas-party-2026?utm_source=copyToPasteBoard&utm_medium=product-links&utm_content=web';

/* ============================================================
   Early-Guests auto-expiration — 2026-10-15 00:00 America/New_York.
   Checkout action is gated on this value, not only visuals.
   ============================================================ */
const EARLY_GUESTS_EXPIRES_UTC_MS = Date.parse('2026-10-15T04:00:00Z'); // 00:00 EDT = 04:00 UTC

const useEarlyGuestsExpired = () => {
  const [expired, setExpired] = useState(() => Date.now() >= EARLY_GUESTS_EXPIRES_UTC_MS);
  useEffect(() => {
    if (expired) return;
    const id = setInterval(() => {
      if (Date.now() >= EARLY_GUESTS_EXPIRES_UTC_MS) {
        setExpired(true);
        clearInterval(id);
      }
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [expired]);
  return expired;
};

/* ============================================================
   Pass
   ============================================================ */
const Pass = ({
  tier, name, price, priceNote, status, statusOpen,
  testId, variant, soldOut,
}) => {
  const handleClick = (e) => { if (soldOut) e.preventDefault(); };

  return (
    <div
      className={`lux-pass lux-pass--${variant}${soldOut ? ' lux-pass--sold' : ''}`}
      data-testid={testId}
      aria-disabled={soldOut || undefined}
    >
      <div className="lux-pass__inner">
        <div className="lux-pass__tier">{tier}</div>
        <div className="lux-pass__name">{name}</div>

        <div className="lux-pass__divider" aria-hidden="true" />

        <div className="lux-pass__price">{price}</div>
        <div className="lux-pass__price-note">{priceNote}</div>

        <div className="lux-pass__status-row">
          <div
            className={
              'lux-pass__status' +
              (soldOut ? ' lux-pass__status--sold' : '') +
              (statusOpen && !soldOut ? ' lux-pass__status--open' : '')
            }
          >
            <span>●</span>
            <span>{status}</span>
          </div>
        </div>

        {soldOut ? (
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="lux-pass__cta lux-pass__cta--sold"
            data-testid={`${testId}-cta`}
          >
            Sold Out
          </button>
        ) : (
          <a
            href={SHOPIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="lux-pass__cta"
            data-testid={`${testId}-cta`}
            onClick={handleClick}
          >
            Get Tickets
          </a>
        )}
      </div>

      {soldOut && (
        <>
          <svg
            className="lux-pass__x"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`xg-${testId}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%"  stopColor="rgba(188, 24, 24, 0.0)" />
                <stop offset="15%" stopColor="rgba(188, 24, 24, 0.92)" />
                <stop offset="50%" stopColor="rgba(240, 72, 72, 0.98)" />
                <stop offset="85%" stopColor="rgba(188, 24, 24, 0.92)" />
                <stop offset="100%" stopColor="rgba(188, 24, 24, 0.0)" />
              </linearGradient>
            </defs>
            <line x1="0" y1="0" x2="100" y2="100"
                  stroke={`url(#xg-${testId})`} strokeWidth="0.9" />
            <line x1="100" y1="0" x2="0" y2="100"
                  stroke={`url(#xg-${testId})`} strokeWidth="0.9" />
          </svg>
          <span className="sr-only">This ticket tier is sold out.</span>
        </>
      )}
    </div>
  );
};

const TicketSection = () => {
  const earlyGuestsSoldOut = useEarlyGuestsExpired();

  return (
    <section id="tickets" className="lux-tickets" data-testid="tickets-section">
      <div className="lux-tickets__texture" aria-hidden="true" />

      <div className="lux-tickets__intro">
        <div className="lux-eyebrow">
          <span className="lux-rule" aria-hidden="true" />
          Secure Your Attendance
          <span className="lux-rule" aria-hidden="true" />
        </div>
        <h2 className="lux-heading">Ticket Options</h2>
        <p className="lux-sub">
          Only the first 200 people who secure their attendance will be admitted to this event.
        </p>
      </div>

      <div className="lux-tickets__grid">
        <Pass
          tier="Tier One"
          name="Early Guests"
          price="$85"
          priceNote="per person · all-inclusive"
          status={earlyGuestsSoldOut ? 'Sold Out' : 'Ends October 14'}
          statusOpen={!earlyGuestsSoldOut}
          testId="early-bird-ticket"
          variant="one"
          soldOut={earlyGuestsSoldOut}
        />
        <Pass
          tier="Tier Two"
          name="Exclusive Guests"
          price="$120"
          priceNote="per person · all-inclusive"
          status="Limited"
          statusOpen
          testId="general-admission-ticket"
          variant="two"
        />
        <Pass
          tier="Tier Three"
          name="VIP Guests"
          price="$250"
          priceNote="per person · all-inclusive"
          status="Limited"
          testId="vip-ticket"
          variant="three"
          soldOut
        />
      </div>

      <p className="lux-tickets__disclaimer" data-testid="ticket-disclaimer">
        All ticket sales are final · Early Guests pricing expires October 14, 2026 at 11:59 PM EST
      </p>
    </section>
  );
};

export default TicketSection;
